// Path: ./src/app/components/FlyerGridList/index.tsx
import * as React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Backdrop, CircularProgress, IconButton, Tooltip } from "@mui/material";
import SelectComponent from "../SelectMui";
import { ImageHoverTitleWrapper } from "../ImageHoverTitle";
import AddIcon from "@mui/icons-material/Add";
import {
  GfImage,
  GfImageWithUsageCount,
} from "@swagger/typescript-fetch-goflyer";
import { useSortedGfImageResults } from "../../../hooks/useSortedGfImageResults";
import {
  PaginationWrapper,
  MuiPagination,
  ImageWrapper,
  Title,
  ImageIcon,
  Item,
  NoImageFoundWrapper,
  HelperTextContainer,
  HelperText,
} from "./styled";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { ButtonComponent } from "../Button";
import StraightIcon from "@mui/icons-material/Straight";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ImagePreviewModal from "../ImagePreviewModal";
import {
  DateFormat,
  S3Size,
  UsageIndicator,
  UsageCountBadge,
} from "../../../utils/commonStyledComponents/commonStyled";
import { formatDateToDayMonthYear, formatFileSize } from "../../../utils/utils";
import LazyImage from "../LazyImage";
import { PermissionComponentWrapper } from "../PermissionComponentWrapper";
import { ImageTagsOverlay } from "../ImageTagsOverlay";
import { useShowImageTagsInSearch } from "../../../hooks/useShowImageTagsInSearch";

interface Props {
  itemsList?: GfImageWithUsageCount[];
  count?: number;
  page?: number;
  rowsPerPageList?: {
    label: string;
    value: number;
  }[];
  rowsPerPage?: number | string;
  handlePaginationChange?: (
    // eslint-disable-next-line
    event: React.ChangeEvent<unknown>,
    // eslint-disable-next-line
    value: number,
  ) => void;
  /**
   * Handles the click event on a product image.
   * when clicked on product image this function will be executed
   * @param value The value associated with the clicked item (product).
   */
  // eslint-disable-next-line
  onItemClick?: (value: GfImage) => void;
  // eslint-disable-next-line
  onRowPerPageSelect?: (value: string | number) => void;
  dataTestId?: string;
  isLoading?: boolean;
  searchText?: string;
  // eslint-disable-next-line
  onTagEdit?: (image: GfImage) => void;
}

/**
 * this is the image list after a admin first time search image to create a flyer item
 * @param param0
 * @returns
 */
export function FlyerGridList({
  itemsList,
  count,
  handlePaginationChange,
  page,
  rowsPerPageList,
  rowsPerPage,
  onRowPerPageSelect,
  onItemClick,
  dataTestId,
  isLoading = false,
  searchText = "",
  onTagEdit,
}: Props) {
  const { t } = useTranslation();
  const showImageTags = useShowImageTagsInSearch();
  const sortedItemsList = useSortedGfImageResults({
    itemsList,
    searchText,
  });

  return (
    <Box
      sx={{
        flexDirection: "column",
        justifyContent: "space-between",
        display: "flex",
        alignItems: "center",
        position: "relative",
      }}
      data-testid={dataTestId}
    >
      <Grid
        container
        columns={{ xs: 2, sm: 4, md: 5, lg: 8 }}
        rowSpacing={0}
        style={{
          marginBottom: 20,
          height: "625px",
          overflowY: "scroll",
          background: "#eeeeee",
          paddingTop: 30,
          paddingBottom: 30,
          boxShadow: " inset 0 0 10px #b7b7b7a1",
          borderRadius: "8px",
        }}
      >
        {sortedItemsList?.map((value, index) => (
          <Grid item xs={1} sm={1} md={1} key={index}>
            <Item>
              <ImagePreviewModal src={value.src}>
                <Item
                  onClick={() => {
                    onItemClick && onItemClick(value); //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) need to find out what it is clicking
                  }}
                  data-testid={`${dataTestId}-image`}
                >
                  <ImageHoverTitleWrapper text={value.alt} delay={500}>
                    <ImageWrapper
                      hasUsageHistory={(value?.flyerItemUsageCount || 0) > 0}
                    >
                      {(value?.flyerItemUsageCount || 0) > 0 && (
                        <UsageIndicator />
                      )}
                      <ImageIcon>
                        <AddIcon style={{ fontSize: 15 }} />
                      </ImageIcon>
                      <LazyImage
                        src={value?.srcSmall || value?.src}
                        alt={value.alt}
                        enableFadeIn={true}
                        rootMargin="100px"
                        threshold={0.1}
                        data-testid={`${dataTestId}-lazy-image`}
                      />
                      {showImageTags && (
                        <ImageTagsOverlay
                          imageTags={value.imageTags}
                          dataTestId={`${dataTestId}-image-tags`}
                        />
                      )}
                      <DateFormat data-testid={`${dataTestId}-date`}>
                        {formatDateToDayMonthYear(
                          value?.imageCreationDate ||
                            value?.s3LastModified ||
                            value?.createdAt,
                        )}
                      </DateFormat>
                      {value?.s3Size && (
                        <S3Size data-testid={`${dataTestId}-size`}>
                          {formatFileSize(value?.s3Size)}
                        </S3Size>
                      )}
                      {(value?.flyerItemUsageCount || 0) > 0 && (
                        <UsageCountBadge
                          count={value?.flyerItemUsageCount || 0}
                          data-testid={`${dataTestId}-usage-count`}
                          title={`Used in ${
                            value?.flyerItemUsageCount || 0
                          } flyer item${
                            (value?.flyerItemUsageCount || 0) !== 1 ? "s" : ""
                          }`}
                        >
                          {value?.flyerItemUsageCount}
                        </UsageCountBadge>
                      )}
                      {onTagEdit && (
                        <PermissionComponentWrapper
                          requiredPermission={["update"]}
                          requiredPermissionGroup={["imageTag"]}
                          wrapperCss={`
                          display:block;
                          margin-bottom:5px;
                          margin-top: 5px;
                          `}
                        >
                          <div
                            className="tag-edit-btn"
                            style={{
                              position: "absolute",
                              bottom: 4,
                              right: 4,
                              zIndex: 3,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              onTagEdit(value);
                            }}
                          >
                            <Tooltip title="Edit Image Tags" placement="top">
                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: "rgba(255,255,255,0.9)",
                                  padding: "3px",
                                  "&:hover": { bgcolor: "white" },
                                }}
                              >
                                <LocalOfferIcon
                                  sx={{ fontSize: 14, color: "#f58220" }}
                                />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </PermissionComponentWrapper>
                      )}
                    </ImageWrapper>
                    <Title data-testid={`${dataTestId}-title`}>
                      {value.alt}
                    </Title>
                  </ImageHoverTitleWrapper>
                </Item>
              </ImagePreviewModal>
            </Item>
          </Grid>
        ))}

        {!sortedItemsList?.length && searchText?.length > 0 && !isLoading ? (
          <NoImageFoundWrapper>
            <div>
              <h1>{t(messages.NO_IMAGE_FOUND())}</h1>
              <ButtonComponent
                testId="create-flyer-item-button"
                title={`${t(messages.Create_Flyer_Item())}`}
                onClick={async () => {
                  const date = new Date();
                  onItemClick &&
                    onItemClick({
                      id: "",
                      src: "",
                      createdAt: date,
                      updatedAt: date,
                      isDeleted: false,
                    });
                }}
              ></ButtonComponent>
            </div>
          </NoImageFoundWrapper>
        ) : !sortedItemsList?.length && searchText?.length === 0 ? (
          <NoImageFoundWrapper>
            <HelperTextContainer>
              <StraightIcon style={{ fontSize: "150px" }} />
              <HelperText>
                {t(messages.SEARCH_SELECT_CREATE_FLYER())}
                <br />
                {t(messages.CREATE_FLYER_OWN_IMAGE())}
              </HelperText>
              <ButtonComponent
                testId="create-flyer-item-button"
                title={`${t(messages.Create_Flyer_Item())}`}
                onClick={async () => {
                  const date = new Date();
                  onItemClick &&
                    onItemClick({
                      id: "",
                      src: "",
                      createdAt: date,
                      updatedAt: date,
                      isDeleted: false,
                    });
                }}
              ></ButtonComponent>
            </HelperTextContainer>
          </NoImageFoundWrapper>
        ) : (
          <></>
        )}
      </Grid>
      <PaginationWrapper>
        {rowsPerPageList ? (
          <SelectComponent
            title="Rows per page:"
            selectOptions={rowsPerPageList}
            selectedValue={rowsPerPage}
            onSelect={onRowPerPageSelect}
            wrapperStyle={`
                flex-direction: row;
                align-items: center;
                justify-content: start;
                `}
            borderColor={{
              normal: "#dedede",
            }}
          ></SelectComponent>
        ) : (
          <></>
        )}

        <MuiPagination
          count={count}
          page={page}
          onChange={handlePaginationChange}
        />
      </PaginationWrapper>
      <Backdrop
        sx={{
          color: "#ff813a",
          zIndex: 9,
          position: "absolute",
          backgroundColor: "rgb(0 0 0 / 18%)",
          borderRadius: "10px",
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}
